/* The PDP catalog section (designs/pdp-catalog.md PC1, PC14, PC16 and the UI
   section) — the Product Development Plan defined once: the deliverables
   matrix, the per-phase exit criteria and the gates, on three tabs of one
   section. Reads render from the store's one PDP fault domain; writes go
   through pdp.actions and re-read. Every slot is in one of the three honest
   states, every row carries its stamp, and only the catalog authority (Admin)
   sees Add / Edit / Retire — the server refuses anyone else regardless. */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { useAppState, type Store } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { LoadError } from "../shell/LoadError";
import { Loading } from "../shell/Loading";
import type { ClientState } from "../shell/state";
import type { Stamp } from "../../../shared/organizer-roles";
import { nearMatches, type Role } from "../../../shared/roles";
import {
  IMPORT_SRC,
  PDP_CRITERION_AREAS,
  PDP_PHASE_NAMES,
  PDP_PHASES,
  PDP_VERSION,
  standing,
  validatePdpCriterionEdit,
  validatePdpCriterionInput,
  validatePdpDeliverableEdit,
  validatePdpDeliverableInput,
  validatePdpGateEdit,
  type PdpCriterion,
  type PdpCriterionEdit,
  type PdpCriterionInput,
  type PdpDeliverable,
  type PdpDeliverableEdit,
  type PdpDeliverableInput,
  type PdpGate,
  type PdpGateEdit,
  type PdpPhase,
  type PdpTarget,
} from "../../../shared/pdp";
import { createCriterion, createDeliverable, patchCriterion, patchDeliverable, patchGate } from "./pdp.actions";
import { ROLE_LIST_ID, RoleChips, resolveRole } from "./pdp/RoleChips";

/* ---- shared bits ---- */

/* an ISO stamp renders as its UTC calendar day — the import stamp is a date,
   not an instant, and a container's clock must not move it */
const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

/* PC14: the import's 100-character source folds to two words with the full
   string as the title; anything else names its recorder and date */
export const stampLabel = (s: Stamp): { text: string; title: string | undefined } =>
  s.source === IMPORT_SRC
    ? { text: `import · ${fmtDate(s.date)}`, title: IMPORT_SRC }
    : { text: `${s.by?.name ?? s.source} · ${fmtDate(s.date)}`, title: undefined };

function StampChip({ stamp }: { stamp: Stamp }): JSX.Element {
  const { text, title } = stampLabel(stamp);
  return (
    <span className="stamp" title={title}>
      {text}
    </span>
  );
}

const Missing = (): JSX.Element => <span className="state missing">not recorded</span>;

const nameOf = (roles: Role[], id: string): string => roles.find((r) => r.id === id)?.name ?? id;

/* role ids render as names joined by " / "; an empty list is the red gap (PC6) */
function RoleNames({ ids, roles }: { ids: string[]; roles: Role[] }): JSX.Element {
  return ids.length ? <>{ids.map((id) => nameOf(roles, id)).join(" / ")}</> : <Missing />;
}

/* the first three near-matches, then "+n more" (UI/UX review, should #6) */
const NEAR_CAP = 3;
export const nearMatchLine = (hits: PdpDeliverable[]): string => {
  const shown = hits
    .slice(0, NEAR_CAP)
    .map((h) => `${h.swimlane} · ${h.path} · ${h.name} (Phase ${h.phase})`)
    .join(", ");
  const more = hits.length > NEAR_CAP ? `, … +${hits.length - NEAR_CAP} more` : "";
  return `Already in the catalog: ${shown}${more} — add yours anyway if it is a different deliverable.`;
};

const TARGET_SYMBOL: Record<PdpTarget["op"], string> = { ">=": "≥", "<=": "≤", ">": ">", "<": "<" };
const targetText = (t: PdpTarget): string => `${TARGET_SYMBOL[t.op]} ${t.value} ${t.unit}`;

/* a form-level refusal is brought to the reader (UI review): focusable alert */
function FormError({ error }: { error: string | null }): JSX.Element {
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (error) ref.current?.focus();
  }, [error]);
  return (
    <p className="form-error" role="alert" tabIndex={-1} ref={ref}>
      {error ?? ""}
    </p>
  );
}

const currentRoles = (roles: Role[]): Role[] => roles.filter((r) => !r.retired);

/* ---- the section ---- */

export function PdpView(): JSX.Element {
  const { state, store } = useAppState();
  if (state.pdpError) return <LoadError what="PDP catalog" />;
  if (state.pdpPending) return <Loading what="the PDP catalog" />;
  const rows = standing(state.pdp.deliverables);
  const tab = state.area ?? "Deliverables";
  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>PDP {PDP_VERSION}.</b>
          <span>
            {rows.length} deliverables · {PDP_PHASES.length} phases · {state.pdp.gates.length} gates · imported Sep
            22, 2026 from "PDP 2.5 Engineering Deliverables Matrix - DRAFT.xlsx"
          </span>
        </div>
      }
      footnote="The PDP is one org-wide template every product is measured against. Every row carries who recorded it and when; the import is the first revision of every row."
    >
      <datalist id={ROLE_LIST_ID}>
        {currentRoles(state.roles).map((r) => (
          <option key={r.id} value={r.name} />
        ))}
      </datalist>
      {tab === "Gates" ? (
        <GatesTab state={state} store={store} />
      ) : tab === "Phases & exit criteria" ? (
        <PhasesTab state={state} store={store} />
      ) : (
        <DeliverablesTab state={state} store={store} />
      )}
    </ViewFrame>
  );
}

/* ======================= Deliverables ======================= */

type PhaseFilter = PdpPhase | "all";
type Standing = "current" | "retired" | "all";
type FormMode = { mode: "add" } | { mode: "edit"; id: string } | null;

const admits = (row: PdpDeliverable, filter: Standing): boolean =>
  filter === "all" || (filter === "retired" ? row.retired === true : !row.retired);

const byMatrix = (a: PdpDeliverable, b: PdpDeliverable): number =>
  a.swimlane.localeCompare(b.swimlane) ||
  a.path.localeCompare(b.path) ||
  a.phase - b.phase ||
  a.name.toLowerCase().localeCompare(b.name.toLowerCase());

function DeliverablesTab({ state, store }: { state: ClientState; store: Store }): JSX.Element {
  const all = state.pdp.deliverables;
  const [search, setSearch] = useState("");
  const [phase, setPhase] = useState<PhaseFilter>("all");
  const [circulation, setCirculation] = useState<Standing>("current");
  /* rows retired or brought back in this session stay in view under the
     current filter until it changes (UI/UX review, should #8) */
  const [pinned, setPinned] = useState<string[]>([]);
  const [form, setForm] = useState<FormMode>(null);
  /* a one-field PATCH has no form to carry a refusal — it lands under the row (review #4) */
  const [rowError, setRowError] = useState<{ id: string; reason: string } | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const narrowed = (rows: PdpDeliverable[]): PdpDeliverable[] => {
    const byPhase = phase === "all" ? rows : rows.filter((r) => r.phase === phase);
    return search.trim() ? nearMatches(byPhase, search) : byPhase;
  };
  const admitted = all.filter((r) => admits(r, circulation) || pinned.includes(r.id));
  const shown = narrowed(admitted).sort(byMatrix);
  const hiddenRetired = circulation === "current" ? narrowed(all.filter((r) => r.retired && !pinned.includes(r.id))).length : 0;

  const chooseCirculation = (next: Standing): void => {
    setCirculation(next);
    setPinned([]);
    setRowError(null);
  };
  const open = (next: FormMode, from: HTMLElement): void => {
    opener.current = from;
    setRowError(null);
    setForm(next);
  };
  const close = (): void => {
    setForm(null);
    opener.current?.focus();
  };
  const toggleRetire = async (row: PdpDeliverable): Promise<void> => {
    setPinned((p) => (p.includes(row.id) ? p : [...p, row.id]));
    const refused = await patchDeliverable(store, row.id, { retired: !row.retired });
    setRowError(refused ? { id: row.id, reason: refused } : null);
  };

  /* group → subgroup → rows, in one flat list of table rows */
  const groups = new Map<string, Map<string, PdpDeliverable[]>>();
  for (const row of shown) {
    const lane = groups.get(row.swimlane) ?? new Map<string, PdpDeliverable[]>();
    lane.set(row.path, [...(lane.get(row.path) ?? []), row]);
    groups.set(row.swimlane, lane);
  }
  const columns = state.catalogAuthority ? 9 : 8;
  const emptyLine = `No deliverable matches — clear a filter${state.catalogAuthority ? " or add it" : ""}.${
    hiddenRetired ? ` ${hiddenRetired} retired rows hidden — choose Retired or All.` : ""
  }`;

  return (
    <section id="pdp-deliverables">
      <div className="pdp-toolbar">
        <label className="sr-only" htmlFor="pdp-search">
          Filter deliverables by name
        </label>
        <input
          id="pdp-search"
          type="search"
          placeholder="Filter deliverables by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span id="pdp-count" className="stamp">
          {shown.length} of {all.length} shown
        </span>
        <div className="seg-ctl" id="pdp-phase-filter" role="group" aria-label="Phase">
          <button type="button" data-phase="all" aria-pressed={phase === "all"} onClick={() => setPhase("all")}>
            All phases
          </button>
          {PDP_PHASES.map((p) => (
            <button key={p} type="button" data-phase={p} aria-pressed={phase === p} onClick={() => setPhase(p)}>
              {p}
            </button>
          ))}
        </div>
        <div className="seg-ctl" id="pdp-standing-filter" role="group" aria-label="Circulation">
          {(["current", "retired", "all"] as Standing[]).map((c) => (
            <button
              key={c}
              type="button"
              data-standing={c}
              aria-pressed={circulation === c}
              onClick={() => chooseCirculation(c)}
            >
              {c === "current" ? "Current" : c === "retired" ? "Retired" : "All"}
            </button>
          ))}
        </div>
        {state.catalogAuthority ? (
          <button
            id="add-deliverable"
            type="button"
            className="btn"
            onClick={(e) => open({ mode: "add" }, e.currentTarget)}
          >
            Add deliverable…
          </button>
        ) : (
          <div className="slot-note" id="no-catalog-authority">
            Changing the PDP catalog needs the Admin organizer role.
          </div>
        )}
      </div>
      {form?.mode === "add" ? (
        <DeliverableForm state={state} store={store} current={null} onDone={close} />
      ) : null}
      <table className="pdp-matrix">
        <thead>
          <tr>
            <th>Swimlane</th>
            <th>Path</th>
            <th>Deliverable</th>
            <th>Phase</th>
            <th>Owner</th>
            <th>Reviewer</th>
            <th>Consulted</th>
            <th>Recorded</th>
            {state.catalogAuthority ? <th aria-label="Actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr>
              <td className="pdp-empty" colSpan={columns}>
                {emptyLine}
              </td>
            </tr>
          ) : null}
          {[...groups.entries()].flatMap(([lane, paths]) => [
            <tr className="pdp-group" data-swimlane={lane} key={`g:${lane}`}>
              <td colSpan={columns}>
                {lane} · {[...paths.values()].reduce((n, rows) => n + rows.length, 0)}
              </td>
            </tr>,
            ...[...paths.entries()].flatMap(([path, rows]) => [
              <tr className="pdp-subgroup" key={`p:${lane}:${path}`}>
                <td colSpan={columns}>{path}</td>
              </tr>,
              ...rows.flatMap((row) => [
                form?.mode === "edit" && form.id === row.id ? (
                  <tr className="pdp-editing" key={row.id}>
                    <td colSpan={columns}>
                      <DeliverableForm state={state} store={store} current={row} onDone={close} />
                    </td>
                  </tr>
                ) : (
                  <DeliverableRow
                    key={row.id}
                    row={row}
                    state={state}
                    onEdit={(from) => open({ mode: "edit", id: row.id }, from)}
                    onToggleRetire={() => void toggleRetire(row)}
                  />
                ),
                ...(rowError?.id === row.id
                  ? [
                      <tr className="pdp-row-error" key={`${row.id}:error`}>
                        <td colSpan={columns}>
                          <p className="form-error" role="alert">
                            {rowError.reason}
                          </p>
                        </td>
                      </tr>,
                    ]
                  : []),
              ]),
            ]),
          ])}
        </tbody>
      </table>
    </section>
  );
}

function DeliverableRow({
  row,
  state,
  onEdit,
  onToggleRetire,
}: {
  row: PdpDeliverable;
  state: ClientState;
  onEdit: (from: HTMLElement) => void;
  onToggleRetire: () => void;
}): JSX.Element {
  const roles = state.roles;
  return (
    <tr className={"pdp-row" + (row.retired ? " retired" : "")} data-id={row.id}>
      <td data-col="Swimlane" className="pdp-lane">
        {row.swimlane}
      </td>
      <td data-col="Path" className="pdp-path">
        {row.path}
      </td>
      <td data-col="Deliverable" className="pdp-name">
        {row.name}
        {row.retired ? <span className="state record-status status-archived">retired</span> : null}
      </td>
      <td data-col="Phase" className="pdp-phase">
        {row.phase}
        {row.iterate ? <span className="state iterate">iterates</span> : null}
      </td>
      <td data-col="Owner">
        <RoleNames ids={row.ownerRoleIds} roles={roles} />
      </td>
      <td data-col="Reviewer">
        <RoleNames ids={row.reviewerRoleIds} roles={roles} />
      </td>
      <td data-col="Consulted">
        <RoleNames ids={row.consultedRoleIds} roles={roles} />
      </td>
      <td data-col="Recorded" className="pdp-recorded">
        <StampChip stamp={row.recorded} />
      </td>
      {state.catalogAuthority ? (
        <td className="pdp-actions">
          <button type="button" className="edit-deliverable btn ghost small" onClick={(e) => onEdit(e.currentTarget)}>
            Edit
          </button>
          <button type="button" className="retire-deliverable btn ghost small" onClick={onToggleRetire}>
            {row.retired ? "Bring back" : "Retire"}
          </button>
        </td>
      ) : null}
    </tr>
  );
}

/* the add / edit form (the Roles inline idiom): the shared validator says the
   reason sooner, the server's refusal is shown verbatim, and an edit sends
   only what changed (PC11) */
function DeliverableForm({
  state,
  store,
  current,
  onDone,
}: {
  state: ClientState;
  store: Store;
  current: PdpDeliverable | null;
  onDone: () => void;
}): JSX.Element {
  const rows = state.pdp.deliverables;
  const roles = state.roles;
  const [swimlane, setSwimlane] = useState(current?.swimlane ?? "");
  const [path, setPath] = useState(current?.path ?? "");
  const [name, setName] = useState(current?.name ?? "");
  const [phase, setPhase] = useState<PdpPhase>(current?.phase ?? 0);
  const [iterate, setIterate] = useState(current?.iterate ?? false);
  const [owners, setOwners] = useState<string[]>(current?.ownerRoleIds ?? []);
  const [reviewers, setReviewers] = useState<string[]>(current?.reviewerRoleIds ?? []);
  const [consulted, setConsulted] = useState<string[]>(current?.consultedRoleIds ?? []);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);

  const collapse = (t: string): string => t.trim().replace(/\s+/g, " ");
  const input: PdpDeliverableInput = {
    swimlane: collapse(swimlane),
    path: collapse(path),
    name: collapse(name),
    phase,
    iterate,
    ownerRoleIds: owners,
    reviewerRoleIds: reviewers,
    consultedRoleIds: consulted,
  };
  const swimlanes = [...new Set(standing(rows).map((r) => r.swimlane))].sort();
  const paths = [...new Set(standing(rows).filter((r) => r.swimlane === collapse(swimlane)).map((r) => r.path))].sort();
  const near = name.trim() ? nearMatches(standing(rows).filter((r) => r.id !== current?.id), name) : [];

  const save = async (): Promise<void> => {
    if (saving) return;
    let reason: string | null;
    let body: PdpDeliverableEdit | PdpDeliverableInput = input;
    if (current) {
      const edit: PdpDeliverableEdit = {};
      const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
      for (const key of Object.keys(input) as (keyof PdpDeliverableInput)[])
        if (!same(input[key], current[key])) (edit as Record<string, unknown>)[key] = input[key];
      /* nothing changed: close without minting a revision that only re-stamps (review #6) */
      if (Object.keys(edit).length === 0) return onDone();
      reason = validatePdpDeliverableEdit(edit, rows, roles, current.id);
      body = edit;
    } else reason = validatePdpDeliverableInput(input, rows, roles);
    if (reason) return setError(reason);
    setError(null);
    setSaving(true);
    const refused = current
      ? await patchDeliverable(store, current.id, body as PdpDeliverableEdit)
      : await createDeliverable(store, input);
    setSaving(false);
    if (refused) return setError(refused);
    onDone();
  };
  const onKey = (e: KeyboardEvent<HTMLElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onDone();
    }
  };

  return (
    <section className="card confirmed" id="deliverable-form" onKeyDown={onKey}>
      <div className="card-head">
        <div className="title">{current ? `Edit ${current.name}` : "Add a deliverable"}</div>
      </div>
      <div className="card-form">
        <label className="field">
          <span className="field-label">Swimlane</span>
          <input id="pdp-swimlane" ref={first} type="text" list="pdp-swimlanes" value={swimlane} onChange={(e) => setSwimlane(e.target.value)} />
          <datalist id="pdp-swimlanes">
            {swimlanes.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span className="field-label">Path</span>
          <input id="pdp-path" type="text" list="pdp-paths" value={path} onChange={(e) => setPath(e.target.value)} />
          <datalist id="pdp-paths">
            {paths.map((p) => (
              <option key={p} value={p} />
            ))}
          </datalist>
        </label>
        <label className="field">
          <span className="field-label">Deliverable</span>
          <input id="pdp-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        {near.length ? (
          <div className="slot-note" id="pdp-near-matches">
            {nearMatchLine(near)}
          </div>
        ) : null}
        <label className="field">
          <span className="field-label">Phase</span>
          <select id="pdp-phase" value={phase} onChange={(e) => setPhase(Number(e.target.value) as PdpPhase)}>
            {PDP_PHASES.map((p) => (
              <option key={p} value={p}>
                {p} · {PDP_PHASE_NAMES[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="field pdp-iterate-row">
          <input id="pdp-iterate" type="checkbox" checked={iterate} onChange={(e) => setIterate(e.target.checked)} />
          <span>Owed again in every later phase</span>
        </label>
        <RoleChips id="pdp-owners" label="Owner" value={owners} roles={roles} onChange={setOwners} />
        <RoleChips id="pdp-reviewers" label="Reviewer" value={reviewers} roles={roles} onChange={setReviewers} />
        <RoleChips id="pdp-consulted" label="Consulted" value={consulted} roles={roles} onChange={setConsulted} />
      </div>
      <FormError error={error} />
      <div className="form-actions">
        <button id="pdp-save" type="button" className="btn small" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save deliverable"}
        </button>
        <button id="pdp-cancel" type="button" className="btn ghost small" onClick={onDone}>
          Cancel
        </button>
      </div>
    </section>
  );
}

/* ======================= Phases & exit criteria ======================= */

function PhasesTab({ state, store }: { state: ClientState; store: Store }): JSX.Element {
  const [editing, setEditing] = useState<{ phase: PdpPhase; area: string } | null>(null);
  return (
    <div id="pdp-phases">
      {PDP_PHASES.map((phase) => {
        const mine = state.pdp.criteria.filter((c) => c.phase === phase);
        const recorded = mine.filter((c) => c.text !== null).length;
        const gate = state.pdp.gates.find((g) => g.afterPhase === phase);
        const heading = `pdp-phase-${phase}-h`;
        return (
          <section className="area-panel pdp-phase" id={`pdp-phase-${phase}`} aria-labelledby={heading} key={phase}>
            <h3 id={heading}>
              Phase {phase} <span className="pdp-phase-name">· {PDP_PHASE_NAMES[phase]}</span>
              <span className="stamp">
                {recorded} of {PDP_CRITERION_AREAS.length} recorded
              </span>
              {gate ? <span className="stamp">→ {gate.label}</span> : null}
            </h3>
            <ul className="slots">
              {PDP_CRITERION_AREAS.map((area) => {
                const row = mine.find((c) => c.area === area) ?? null;
                const open = editing?.phase === phase && editing.area === area;
                return (
                  <li className="pdp-criterion" data-area={area} data-id={row?.id} key={area}>
                    {open ? (
                      <CriterionForm phase={phase} area={area} current={row} store={store} state={state} onDone={() => setEditing(null)} />
                    ) : (
                      <>
                        <span className="slot-main">
                          <span className="slot-name">{area}</span>
                          {row && row.text !== null ? (
                            <div className="val">
                              {row.text}
                              {row.unconfirmed ? <span className="state unconfirmed">unconfirmed</span> : null}
                              {row.target ? <code className="pdp-target">{targetText(row.target)}</code> : null}
                            </div>
                          ) : (
                            <div className="val">
                              <Missing />
                            </div>
                          )}
                          {row ? (
                            <div className="slot-note pdp-recorded">
                              <StampChip stamp={row.recorded} />
                            </div>
                          ) : null}
                        </span>
                        {state.catalogAuthority ? (
                          <span className="card-actions">
                            <button type="button" className="edit-criterion btn ghost small" onClick={() => setEditing({ phase, area })}>
                              Edit
                            </button>
                          </span>
                        ) : null}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function CriterionForm({
  phase,
  area,
  current,
  store,
  state,
  onDone,
}: {
  phase: PdpPhase;
  area: string;
  current: PdpCriterion | null;
  store: Store;
  state: ClientState;
  onDone: () => void;
}): JSX.Element {
  const [text, setText] = useState(current?.text ?? "");
  const [unconfirmed, setUnconfirmed] = useState(current?.unconfirmed ?? false);
  const [op, setOp] = useState<string>(current?.target?.op ?? "");
  const [value, setValue] = useState(current?.target ? String(current.target.value) : "");
  const [unit, setUnit] = useState(current?.target?.unit ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);

  /* all three or none — a half-filled target hands the validator a partial
     object, and its one reason covers every partial shape */
  const target = (): PdpTarget | null | Record<string, unknown> => {
    if (!op && !value.trim() && !unit.trim()) return null;
    return {
      ...(op ? { op } : {}),
      ...(value.trim() ? { value: Number(value) } : {}),
      ...(unit.trim() ? { unit: unit.trim() } : {}),
    };
  };

  const save = async (): Promise<void> => {
    if (saving) return;
    const input = { phase, area, text: text.trim() ? text.trim() : null, unconfirmed, target: target() };
    /* an edit sends only what changed; nothing changed closes without a revision (review #6) */
    const edit: Record<string, unknown> = {};
    if (current) {
      const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
      if (!same(input.text, current.text)) edit.text = input.text;
      if (unconfirmed !== current.unconfirmed) edit.unconfirmed = unconfirmed;
      if (!same(input.target, current.target)) edit.target = input.target;
      if (Object.keys(edit).length === 0) return onDone();
    }
    const reason = current
      ? validatePdpCriterionEdit(edit as PdpCriterionEdit, state.pdp.criteria, current.id)
      : validatePdpCriterionInput(input, state.pdp.criteria);
    if (reason) return setError(reason);
    setError(null);
    setSaving(true);
    const refused = current
      ? await patchCriterion(store, current.id, edit as PdpCriterionEdit)
      : await createCriterion(store, input as PdpCriterionInput);
    setSaving(false);
    if (refused) return setError(refused);
    onDone();
  };

  return (
    <div
      className="card-form pdp-criterion-form"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onDone();
        }
      }}
    >
      <span className="slot-name">{area}</span>
      <label className="field">
        <span className="field-label">Criterion</span>
        <textarea id="crit-text" ref={first} rows={2} value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <label className="field pdp-iterate-row">
        <input id="crit-unconfirmed" type="checkbox" checked={unconfirmed} onChange={(e) => setUnconfirmed(e.target.checked)} />
        <span>Unconfirmed — an assumption, not verified yet</span>
      </label>
      <fieldset className="pdp-target-fields">
        <legend>Target (optional)</legend>
        <label className="field">
          <span className="field-label">Operator</span>
          <select id="crit-op" value={op} onChange={(e) => setOp(e.target.value)}>
            <option value=""></option>
            <option value=">=">≥</option>
            <option value="<=">≤</option>
            <option value=">">&gt;</option>
            <option value="<">&lt;</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Value</span>
          <input id="crit-value" type="number" value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Unit</span>
          <input id="crit-unit" type="text" value={unit} placeholder="e.g. % of manned" onChange={(e) => setUnit(e.target.value)} />
        </label>
      </fieldset>
      <FormError error={error} />
      <div className="form-actions">
        <button id="crit-save" type="button" className="btn small" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save criterion"}
        </button>
        <button id="crit-cancel" type="button" className="btn ghost small" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ======================= Gates ======================= */

function GatesTab({ state, store }: { state: ClientState; store: Store }): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);
  const gates = [...state.pdp.gates].sort((a, b) => a.afterPhase - b.afterPhase);
  return (
    <section className="area-panel" id="pdp-gates">
      <ul className="slots">
        {gates.map((gate) => (
          <li className="pdp-gate" data-id={gate.id} key={gate.id}>
            {editing === gate.id ? (
              <GateForm gate={gate} state={state} store={store} onDone={() => setEditing(null)} />
            ) : (
              <>
                <span className="slot-main">
                  <span className="slot-name">
                    {gate.label}
                    <span className="stamp">after Phase {gate.afterPhase}</span>
                  </span>
                  <div className="val">{gate.approverRoleId ? nameOf(state.roles, gate.approverRoleId) : <Missing />}</div>
                  <div className="slot-note pdp-recorded">
                    <StampChip stamp={gate.recorded} />
                  </div>
                </span>
                {state.catalogAuthority ? (
                  <span className="card-actions">
                    <button type="button" className="edit-gate btn ghost small" onClick={() => setEditing(gate.id)}>
                      Edit
                    </button>
                  </span>
                ) : null}
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function GateForm({
  gate,
  state,
  store,
  onDone,
}: {
  gate: PdpGate;
  state: ClientState;
  store: Store;
  onDone: () => void;
}): JSX.Element {
  const [label, setLabel] = useState(gate.label);
  const [approver, setApprover] = useState(gate.approverRoleId ? nameOf(state.roles, gate.approverRoleId) : "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);

  const save = async (): Promise<void> => {
    if (saving) return;
    const edit: PdpGateEdit = {};
    const cleanLabel = label.trim().replace(/\s+/g, " ");
    if (cleanLabel !== gate.label) edit.label = label;
    /* blank = not recorded; a typed name resolves to its id, or is refused
       by the shared validator under the name the reader typed */
    const approverId = approver.trim() ? (resolveRole(state.roles, approver)?.id ?? approver.trim()) : null;
    if (approverId !== gate.approverRoleId) edit.approverRoleId = approverId;
    if (Object.keys(edit).length === 0) return onDone();
    const reason = validatePdpGateEdit(edit, state.pdp.gates, state.roles, gate.id);
    if (reason) return setError(reason);
    setError(null);
    setSaving(true);
    const refused = await patchGate(store, gate.id, edit);
    setSaving(false);
    if (refused) return setError(refused);
    onDone();
  };

  return (
    <div
      className="card-form pdp-gate-form"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onDone();
        }
      }}
    >
      <label className="field">
        <span className="field-label">Label</span>
        <input id="gate-label" ref={first} type="text" value={label} onChange={(e) => setLabel(e.target.value)} />
      </label>
      <label className="field">
        <span className="field-label">Approver role (blank = not recorded)</span>
        <input id="gate-approver" type="text" list={ROLE_LIST_ID} value={approver} onChange={(e) => setApprover(e.target.value)} />
      </label>
      <FormError error={error} />
      <div className="form-actions">
        <button id="gate-save" type="button" className="btn small" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save gate"}
        </button>
        <button id="gate-cancel" type="button" className="btn ghost small" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}
